import React, { useState, FC } from "react";
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { auth } from "../../firebase";
import { getFirebaseErrorMessage } from "./helpers";

interface AccountTabProps {
    user: firebase.User | null;
    scaleFactor?: number;
    setScaleFactor?: (factor: number) => void;
}

const AccountTab: FC<AccountTabProps> = ({ user, scaleFactor, setScaleFactor }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [authError, setAuthError] = useState<string | null>(null);

    const handleEmailSignUp = () => {
        if (!email || !password) {
            setAuthError("Veuillez remplir l'e-mail et le mot de passe.");
            return;
        }
        setAuthError(null);
        auth.createUserWithEmailAndPassword(email, password)
            .catch(error => {
                console.error("Sign up error:", error);
                setAuthError(getFirebaseErrorMessage(error));
            });
    };

    const handleEmailSignIn = () => {
        if (!email || !password) {
            setAuthError("Veuillez remplir l'e-mail et le mot de passe.");
            return;
        }
        setAuthError(null);
        auth.signInWithEmailAndPassword(email, password)
            .catch(error => {
                console.error("Sign in error:", error);
                setAuthError(getFirebaseErrorMessage(error));
            });
    };

    const signInWithGoogle = () => {
        setAuthError(null);
        const provider = new firebase.auth.GoogleAuthProvider();
        auth.signInWithPopup(provider).catch(error => {
            console.error("Authentication error:", error);
            setAuthError(getFirebaseErrorMessage(error));
        });
    };

    const handleSignOut = () => {
        auth.signOut().catch(error => {
            console.error("Sign out error:", error);
        });
    };

    return (
        <div id="panel-account" role="tabpanel" aria-labelledby="tab-account" className="h-full flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900">Mon Compte</h2>
                <p className="text-xs text-gray-500">Gérez votre session</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
                {user ? (
                    <div className="space-y-4">
                        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                            <img src={user.photoURL || undefined} alt="User avatar" className="w-16 h-16 rounded-full" />
                            <div className="min-w-0">
                                <p className="font-bold text-gray-900 truncate">{user.displayName}</p>
                                <p className="text-sm text-gray-600 truncate">{user.email}</p>
                            </div>
                        </div>

                        {/* Scale Control */}
                        {typeof scaleFactor !== 'undefined' && setScaleFactor && (
                            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                                <p className="text-sm font-semibold text-gray-900 mb-3">Échelle de l'interface</p>
                                <div className="flex items-center justify-between gap-4">
                                    <button
                                        onClick={() => setScaleFactor(Math.max(0.5, scaleFactor - 0.1))}
                                        className="w-10 h-10 flex items-center justify-center bg-white border border-gray-300 rounded-lg hover:bg-gray-100 font-bold"
                                    >
                                        -
                                    </button>
                                    <span className="font-mono font-medium text-lg">
                                        {Math.round(scaleFactor * 100)}%
                                    </span>
                                    <button
                                        onClick={() => setScaleFactor(Math.min(1.5, scaleFactor + 0.1))}
                                        className="w-10 h-10 flex items-center justify-center bg-white border border-gray-300 rounded-lg hover:bg-gray-100 font-bold"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        )}
                        <button
                            onClick={handleSignOut}
                            className="w-full bg-red-500 hover:bg-red-600 text-white px-4 py-3 rounded-xl font-bold transition-all"
                        >
                            Déconnexion
                        </button>
                    </div>
                ) : (
                    <div className="text-center p-4">
                        <p className="text-gray-700 mb-6">Connectez-vous ou créez un compte pour commencer.</p>
                        <div className="space-y-4 text-left">
                            <div>
                                <label htmlFor="email" className="text-sm font-medium text-gray-700 block mb-1.5">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
                                    placeholder="votre@email.com"
                                    className="w-full text-sm p-2.5 border border-gray-300 bg-gray-100 text-black rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                                />
                            </div>
                            <div>
                                <label htmlFor="password" className="text-sm font-medium text-gray-700 block mb-1.5">Mot de passe</label>
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
                                    placeholder="••••••••"
                                    className="w-full text-sm p-2.5 border border-gray-300 bg-gray-100 text-black rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                                />
                            </div>

                            {authError && <p className="text-xs text-red-600 text-center pt-2">{authError}</p>}

                            <div className="flex gap-2 pt-2">
                                <button
                                    onClick={handleEmailSignIn}
                                    className="w-full bg-black hover:bg-gray-800 text-white px-4 py-3 rounded-xl font-bold transition-all"
                                >
                                    Connexion
                                </button>
                                <button
                                    onClick={handleEmailSignUp}
                                    className="w-full bg-gray-200 hover:bg-gray-300 text-black px-4 py-3 rounded-xl font-bold transition-all"
                                >
                                    Inscription
                                </button>
                            </div>

                            <div className="relative flex py-2 items-center">
                                <div className="flex-grow border-t border-gray-200"></div>
                                <span className="flex-shrink mx-4 text-gray-400 text-xs">OU</span>
                                <div className="flex-grow border-t border-gray-200"></div>
                            </div>

                            <button
                                onClick={signInWithGoogle}
                                className="w-full bg-white hover:bg-gray-50 border border-gray-300 text-black px-4 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2"
                            >
                                <svg className="w-5 h-5" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512"><path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 126 21.2 177.2 56.4l-64.2 64.2c-31.4-29.3-73.4-48-120.4-48-93.5 0-169.5 75.8-169.5 169.5s76 169.5 169.5 169.5c102.3 0 149.8-74.6 156.9-111.3H248v-85.3h236.1c2.3 12.7 3.9 26.4 3.9 40.8z"></path></svg>
                                Continuer avec Google
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountTab;
